/** @format */

import {DESIGN_STATUS_NOT_REVIEWED, PROOF_STATUS_PENDING} from 'frontier-constants';
import {ProofingContext, proofingDefaultState} from './proofing_context';
import React, {useState, useEffect, useContext} from 'react';
import update from 'immutability-helper';
import {useParams} from 'react-router-dom';
import SuperAgent from 'superagent';

export function ProofingContextProvider({children}) {
    const [state, setState] = useState(proofingDefaultState);
    const {orderId} = useParams();

    async function fetchInitialData() {
        const response = await SuperAgent.get('/account/orders/view/' + orderId).set(
            'accept',
            'application/json'
        );

        const {order, shareLink, shareKey} = response.body;
        // adding a few properties on our state object.
        // status is used to store the individual design status(not reviewed, approved, disapproved)
        let designsOrders = order.designs_orders.map(designOrder => {
            return {
                ...designOrder,
                disapproveReasonId: designOrder.proof_status_reason_id,
                disapproveReason: designOrder.proof_status_reason, // why has this proof been rejected,
                status: designOrder.proof_status_id,
                canSubmitDisapproval: false,
                replacedDesign: [],
                selected: false,
                updating: false, // for single proof operations like rejecting, approving, etc.
                expanded: true, // whether to display the thumbnail on the table or not
                error: null // design specific errors
            };
        });

        const {latest_proof} = order;
        const lastDesignToReview = designsOrders.filter(
            d => d.status === DESIGN_STATUS_NOT_REVIEWED
        );
        const lastDesignToReviewId =
            lastDesignToReview.length === 1 ? lastDesignToReview[0].id : null;

        const updateTarget = {
            $merge: {
                showSubmitModal: latest_proof && latest_proof.status_id === PROOF_STATUS_PENDING,
                complete: latest_proof?.status_id,
                currentProof: latest_proof,
                lastDesignToReviewId,
                loading: false,
                designsOrders,
                shareLink,
                shareKey,
                order
            }
        };

        setState(update(state, updateTarget));
    }

    useEffect(() => {
        fetchInitialData();
    }, []);

    // checks if the user has already approved the specs and notes for the current proof
    // they cannot continue with the review process if they haven't done this first
    function handleGetReviewedSpecsAndNotes() {
        const dataSpecs = localStorage.getItem('order-proofing-specs');
        const dataNotes = localStorage.getItem('order-proofing-notes');

        const updateTarget = {};

        if (dataSpecs) {
            const dataObjSpecs = JSON.parse(dataSpecs || {});
            if (dataObjSpecs.orderId === state.order.id && dataObjSpecs.isVerified) {
                // verify that the proof verified is the latest proof
                if (dataObjSpecs.proofId !== state.currentProof.id) {
                    localStorage.removeItem('order-proofing-specs');
                } else {
                    updateTarget.specsVerification = {
                        $merge: {
                            verifiedOn: dataObjSpecs.verifiedOn,
                            verifiedBy: dataObjSpecs.verifiedBy,
                            isVerified: true
                        }
                    };
                }

                // if there are no comments in this order, approve the comments by default
                if (!state.currentProof.comment && state.currentProof.designs_alerts.length === 0) {
                    updateTarget.reviewNotesVerification = {
                        $merge: {
                            verifiedOn: undefined,
                            verifiedBy: undefined,
                            isVerified: true
                        }
                    };
                }
            }
        }

        // only run if there are comments to approve
        if (
            (!!state.currentProof.comment || state.currentProof.designs_alerts.length > 0) &&
            dataNotes
        ) {
            const dataObjNotes = JSON.parse(dataNotes || {});
            if (dataObjNotes.orderId === state.order.id && dataObjNotes.isVerified) {
                // verify that the proof verified is the latest proof
                if (dataObjNotes.proofId !== state.currentProof.id) {
                    localStorage.removeItem('order-proofing-notes');
                } else {
                    updateTarget.reviewNotesVerification = {
                        $merge: {
                            verifiedOn: dataObjNotes.verifiedOn,
                            verifiedBy: dataObjNotes.verifiedBy,
                            isVerified: true
                        }
                    };
                }
            }
        }

        setState(update(state, updateTarget));
    }

    useEffect(() => {
        if (!state.order?.id) return;

        handleGetReviewedSpecsAndNotes();
    }, [state.order?.id]);

    function handleApproveSpecs(verifiedBy) {
        handleApproveSpecsOrNotes(verifiedBy, 'order-proofing-specs');
    }
    function handleApproveNotes(verifiedBy) {
        handleApproveSpecsOrNotes(verifiedBy, 'order-proofing-notes');
    }

    // saves the verified specs or notes to local storage and updates the state.
    // Without completing this step users cannot submit the proof.
    function handleApproveSpecsOrNotes(verifiedBy, verificationType) {
        const data = {};
        const currentDate = new Date();

        const formattedDate =
            (currentDate.getMonth() + 1).toString().padStart(2, '0') +
            '/' +
            currentDate.getDate().toString().padStart(2, '0') +
            '/' +
            currentDate.getFullYear();

        data.proofId = state.currentProof.id;
        data.verifiedOn = formattedDate;
        data.orderId = state.order.id;
        data.verifiedBy = verifiedBy;
        data.isVerified = true;

        const dataString = JSON.stringify(data);
        localStorage.setItem(verificationType, dataString);
        handleGetReviewedSpecsAndNotes();
    }

    const handleChange = updateTarget => {
        setState(update(state, updateTarget));
    };

    // collapses the thumbnail of a design or all the designs
    const handleCollapse = single => {
        let updateTarget = {};
        if (!single) {
            const collapsedOrders = state.designsOrders.map(d => ({...d, expanded: !d.expanded}));
            updateTarget = {
                designsOrders: {$set: collapsedOrders}
            };
        } else {
            const expandedOrders = state.designsOrders.map(d => ({...d, expanded: !d.expanded}));
            updateTarget = {
                designsOrders: {$set: expandedOrders}
            };
        }
        setState(update(state, updateTarget));
    };

    // updates the disapproval reason for a single design or all the designs
    const handleDesignDisapproveReason = (e, designId) => {
        if (designId) {
            const designOrderIndex = state.designsOrders.findIndex(
                designOrder => designOrder.id === designId
            );
            const updateTarget = {
                designsOrders: {
                    [designOrderIndex]: {
                        disapproveReason: {$set: e.target.value}
                    }
                }
            };
            setState(update(state, updateTarget));
        } else {
            const updateTarget = {
                formData: {
                    disapproveReason: {$set: e.target.value}
                }
            };
            setState(update(state, updateTarget));
        }
    };

    // updates the disapproval reason id for a single design or all the designs
    const handleDesignDisapproveReasonId = (e, designId) => {
        if (designId) {
            const designOrderIndex = state.designsOrders.findIndex(
                designOrder => designOrder.id === designId
            );

            const updateTarget = {
                designsOrders: {
                    [designOrderIndex]: {
                        disapproveReasonId: {$set: e.target.value}, // { $set: 0 }
                        disapproveReason: {$set: ''},
                        replacedDesign: {$set: []}
                    }
                }
            };
            setState(update(state, updateTarget));
        } else {
            const updateTarget = {
                formData: {
                    reasonId: {$set: e.target.value},
                    disapproveReason: {$set: ''}
                }
            };
            setState(update(state, updateTarget));
        }
    };

    // A user might want to replace a design that has been rejected. This function handles that
    const handleReplaceDesign = (newDesign, designId) => {
        const designOrderIndex = state.designsOrders.findIndex(
            designOrder => designOrder.id === designId
        );

        const updateTarget = {
            designsOrders: {
                [designOrderIndex]: {
                    replacedDesign: {$set: newDesign}
                }
            }
        };

        setState(update(state, updateTarget));
    };

    // approves or rejects a single design
    async function handleDesignReview(id, status, disapproveReasonId) {
        if (!id || !status) return;

        const designOrderIndex = state.designsOrders.findIndex(
            designOrder => designOrder.id === id
        );

        const updateTarget = {
            error: {$set: false},
            designsOrders: {
                [designOrderIndex]: {
                    status: {$set: status},
                    updating: {$set: true},
                    error: {$set: null}
                }
            }
        };

        // check if the next design to review is the last one
        const notReviewedDesigns = state.designsOrders.filter(
            d => d.status === DESIGN_STATUS_NOT_REVIEWED && Number(d.id) !== Number(id)
        );
        const isNextDesignLastToReview = notReviewedDesigns.length === 1;

        // the last design to review should needs to happen before the call is made, otherwise the context will not update
        // the last design to review until the response is received. Only the last design to review triggers the
        // confirmation modal, which sends the global proof confirmation, This was causing designs to be
        // dis/ approved but the global proof to remain pending for slow internet connections.
        if (isNextDesignLastToReview) {
            updateTarget.lastDesignToReviewId = {$set: notReviewedDesigns[0].id};
        }

        setState(state => update(state, updateTarget));

        const designOrder = state.designsOrders[designOrderIndex];

        let reason = '';

        if (disapproveReasonId > 0) {
            reason = designOrder.disapproveReason
                ? designOrder.design.id + ' - ' + designOrder.disapproveReason
                : designOrder.design.id + ' - New artwork uploaded';
        }

        let newDesign = designOrder.replacedDesign[0];

        if (Number(state.lastDesignToReviewId) === Number(id)) {
            updateTarget.isSubmitting = {$set: true};
        }

        setState(state => update(state, updateTarget));

        try {
            const response = await SuperAgent.post(
                `/account/proofs/update-design-order-status/${id}`
            )
                .set('accept', 'application/json')
                .send({
                    proof_status_reason_id: parseInt(disapproveReasonId),
                    proof_status_reason: reason,
                    replacedDesign: newDesign,
                    proof_status_id: status
                });

            const updateTarget = {
                designsOrders: {
                    [designOrderIndex]: {
                        $set: {
                            ...response.body.designOrder,
                            disapproveReasonId: response.body.designOrder.proof_status_reason_id,
                            disapproveReason: response.body.designOrder.proof_status_reason,
                            status: response.body.designOrder.proof_status_id,
                            replacedDesign: [],
                            updating: false,
                            expanded: true,
                            error: null
                        }
                    }
                }
            };

            if (response.status === 200) {
                fetchInitialData();
            } else {
                setState(state => update(state, updateTarget));
            }
        } catch (e) {
            console.error(e);
            const updateTarget = {
                designOrderIndex: {error: {$set: e?.response?.body.message}}
            };
            setState(update(state.designsOrders, updateTarget));
        }
    }

    async function handleUndoDesignReview(id) {
        if (!id) return;

        const designOrderIndex = state.designsOrders.findIndex(
            designOrder => designOrder.id === id
        );

        try {
            const response = await SuperAgent.post(
                `/account/proofs/update-design-order-status/${id}`
            )
                .set('accept', 'application/json')
                .send({
                    proof_status_id: DESIGN_STATUS_NOT_REVIEWED,
                    proof_status_reason_id: null,
                    proof_status_reason: ''
                });

            const updateTarget = {
                designsOrders: {
                    [designOrderIndex]: {
                        $set: {
                            ...response.body.designOrder,
                            disapproveReasonId: response.body.designOrder.proof_status_reason_id,
                            disapproveReason: response.body.designOrder.proof_status_reason,
                            status: response.body.designOrder.proof_status_id,
                            replacedDesign: [],
                            updating: false,
                            expanded: true,
                            error: null
                        }
                    }
                }
            };

            // since only the last design to review cannot trigger this function, anything that triggers this function
            // will yield at least 2 designs pending for review.
            updateTarget.lastDesignToReviewId = {$set: null};

            setState(update(state, updateTarget));
        } catch (e) {
            console.error(e);
            const updateTarget = {
                designOrderIndex: {error: {$set: e?.response?.body.message}}
            };
            setState(update(state.designsOrders, updateTarget));
        }
    }

    async function handleApproveAll(proofId) {
        const updateTarget = {
            isSubmitting: {$set: true},
            error: {$set: false}
        };
        setState(update(state, updateTarget));

        const response = await SuperAgent.post(`/account/proofs/approve/${proofId}`).set(
            'accept',
            'application/json'
        );

        try {
            if (response.body.proof.status_id !== PROOF_STATUS_PENDING) {
                fetchInitialData();
            }
        } catch (e) {
            console.error(e);
            const updateTarget = {isSubmitting: false, complete: 0, error: e.response.body.message};
            setState(update(state, updateTarget));
        }
    }

    async function handleRejectAll(proofId) {
        if (
            !state.formData.disapproveReason ||
            state.formData.disapproveReason.trim().length <= 4
        ) {
            const updateTarget = {
                error: {
                    $set: {
                        disapproveReason: {
                            _empty: 'Please provide a reason for disapproval'
                        }
                    }
                }
            };
            setState(state, update(updateTarget));

            return;
        } else {
            const updateTarget = {
                isSubmitting: {$set: true},
                error: {$set: false}
            };
            setState(update(state, updateTarget));
        }

        try {
            const response = await SuperAgent.post(`/account/proofs/disapprove/${proofId}`)
                .set('accept', 'application/json')
                .send({disapproveReason: state.formData.disapproveReason});

            if (response.body.proof.status_id !== PROOF_STATUS_PENDING) {
                fetchInitialData();
            }
        } catch (e) {
            console.error(e);
            const updateTarget = {isSubmitting: false, complete: 0, error: e.response.body.message};
            setState(update(state, updateTarget));
        }
    }

    const methods = {
        handleDesignDisapproveReasonId,
        handleDesignDisapproveReason,
        handleUndoDesignReview,
        handleReplaceDesign,
        handleApproveSpecs,
        handleDesignReview,
        handleApproveNotes,
        handleApproveAll,
        handleRejectAll,
        handleCollapse,
        handleChange
    };

    return (
        <ProofingContext.Provider value={{data: state, ...methods}}>
            {children}
        </ProofingContext.Provider>
    );
}

export const useProofingContext = () => useContext(ProofingContext);
